/**
 * Expansion-side metric formulas. Pure, unit-tested (formulas.test.js).
 * These are benchmark/planning maths that do NOT exist in the product, so they
 * may live client-side. Deal pricing must NOT be added here — it goes through
 * /api/marketing/calc (the product's compute_order_form).
 */

const pct = (x) => (Number.isFinite(x) ? x : 0);
export const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

/** Net & gross revenue retention over a period. All inputs in the same currency. */
export function retention({ startArr, churn = 0, contraction = 0, expansion = 0 }) {
  if (!startArr || startArr <= 0) return { grr: 0, nrr: 0, endArr: 0, lostArr: 0, retainedArr: 0 };
  const lost = Math.min(startArr, churn + contraction);
  const retained = startArr - lost;
  const end = retained + expansion;
  return { grr: pct(retained / startArr), nrr: pct(end / startArr), endArr: end, lostArr: lost, retainedArr: retained };
}

/** How much expansion is needed to hit a target NRR given churn+contraction. */
export function expansionNeededForNrr({ startArr, churn = 0, contraction = 0, targetNrr = 1.1 }) {
  const lost = Math.min(startArr, churn + contraction);
  return Math.max(0, startArr * targetNrr - (startArr - lost));
}

/**
 * Whitespace headroom from catalog penetration.
 *  arr: current ARR of the book; penetration: share (0..1) of catalog value the
 *  average account already owns; capture: share (0..1) of headroom you expect
 *  to sell in the period. Assumes module prices roughly proportional to value.
 */
export function headroom({ arr, penetration, capture = 0.15 }) {
  const p = clamp(penetration, 0.05, 1);
  const unsold = arr * (1 - p) / p;
  const expansion = unsold * clamp(capture, 0, 1);
  return { unsoldAtList: unsold, expansionArr: expansion, nrrPoints: arr > 0 ? expansion / arr : 0 };
}

/**
 * Renewal uplift: incremental ARR from applying a price increase to the share of
 * the book renewing in the period, at a given acceptance rate.
 */
export function renewalUplift({ bookArr, renewingShare, upliftPct, acceptance = 0.8 }) {
  const renewing = bookArr * clamp(renewingShare, 0, 1);
  const accepted = renewing * clamp(acceptance, 0, 1);
  const incremental = accepted * (upliftPct / 100);
  return { renewingArr: renewing, acceptedArr: accepted, incrementalArr: incremental, nrrPoints: bookArr > 0 ? incremental / bookArr : 0 };
}

/** Multi-year TCV factor with uplift compounding on contract anniversaries. */
export function multiYearFactor({ years, upliftPct }) {
  let f = 0; for (let k = 0; k < years; k++) f += Math.pow(1 + upliftPct / 100, k);
  return f;
}

/**
 * Discount leakage beyond policy.
 *  deals: deals per year; avgNegotiated: average negotiated ACV; avgDiscount: average
 *  discount actually given (0..1); policy: threshold (0..1) above which a deal should
 *  route to deal desk; shareOver: share of deals that exceed the threshold.
 * Leakage = list value given away beyond the threshold on over-threshold deals.
 */
export function discountLeakage({ deals, avgNegotiated, avgDiscount, policy, shareOver }) {
  const d = clamp(avgDiscount, 0, 0.95), t = clamp(policy, 0, 0.95), s = clamp(shareOver, 0, 1);
  const list = d < 1 ? avgNegotiated / (1 - d) : avgNegotiated;
  const beyond = Math.max(0, d - t);
  const dealsOver = deals * s;
  return { avgList: list, dealsOver, leakedArr: dealsOver * list * beyond, discountGivenTotal: deals * list * d };
}

/** The five-tool tax: hours per deal across tools × deals × loaded hourly cost. */
export function toolTax({ deals, hoursPerDeal, hourlyCost, reduction = 0.6 }) {
  const hours = deals * hoursPerDeal;
  return { hours, cost: hours * hourlyCost, hoursSaved: hours * clamp(reduction, 0, 1), costSaved: hours * hourlyCost * clamp(reduction, 0, 1) };
}

export const fmtPct = (x, d = 1) => `${(x * 100).toFixed(d)}%`;
